/* eslint-disable react-refresh/only-export-components */
import {
  DimeButton,
  DimeField,
  DimeFieldDescription,
  DimeFieldError,
  DimeFieldLabel,
  DimeSeparator,
  DimeTextarea,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import * as React from 'react';

export const Route = createFileRoute('/design-system/textarea')({
  component: TextareaDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-border bg-background p-6">
      <div className="mx-auto w-full max-w-xs">{children}</div>
    </div>
  );
}

function TextareaDocs() {
  const [value, setValue] = React.useState('');
  const showError = value.length > 0 && value.length < 10;

  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Textarea</h1>
      <p className="mb-8 text-muted-foreground">
        Displays a form textarea or a component that looks like a textarea.
      </p>

      <DocsSection title="Basic">
        <Preview>
          <DimeTextarea placeholder="Type your message here." />
        </Preview>
      </DocsSection>

      <DocsSection title="Trailing adornment">
        <Preview>
          <DimeTextarea
            aria-label="Saved note"
            defaultValue="Bring signage"
            trailing={<Check className="size-4 text-emerald-600" aria-hidden="true" />}
          />
        </Preview>
      </DocsSection>

      <DocsSection title="Field">
        <Preview>
          <DimeField>
            <DimeFieldLabel htmlFor="ta-field">Your message</DimeFieldLabel>
            <DimeTextarea id="ta-field" placeholder="Type your message here." />
            <DimeFieldDescription id="ta-field-hint">
              Your message will be copied to the support team.
            </DimeFieldDescription>
          </DimeField>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">DimeField</code>,{' '}
          <code className="font-mono">DimeFieldLabel</code>, and{' '}
          <code className="font-mono">DimeFieldDescription</code> to create a textarea with a label
          and description.
        </p>
      </DocsSection>

      <DocsSection title="Disabled">
        <Preview>
          <DimeField data-disabled>
            <DimeFieldLabel htmlFor="ta-disabled">Feedback</DimeFieldLabel>
            <DimeTextarea
              id="ta-disabled"
              disabled
              defaultValue="This field is locked and cannot be edited."
            />
          </DimeField>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use the <code className="font-mono">disabled</code> prop on the textarea. Add{' '}
          <code className="font-mono">data-disabled</code> to{' '}
          <code className="font-mono">DimeField</code> to style the label in the disabled state.
        </p>
      </DocsSection>

      <DocsSection title="Invalid">
        <Preview>
          <DimeField data-invalid={showError || undefined}>
            <DimeFieldLabel htmlFor="ta-invalid">Description</DimeFieldLabel>
            <DimeTextarea
              id="ta-invalid"
              value={value}
              onChange={(ev) => setValue(ev.target.value)}
              aria-invalid={showError || undefined}
              aria-describedby="ta-invalid-error"
              placeholder="Describe the issue…"
            />
            {showError && (
              <DimeFieldError id="ta-invalid-error">
                Description must be at least 10 characters.
              </DimeFieldError>
            )}
          </DimeField>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">aria-invalid</code> on the textarea to mark it invalid.
          Add <code className="font-mono">data-invalid</code> to{' '}
          <code className="font-mono">DimeField</code> to style the label. Pair with{' '}
          <code className="font-mono">DimeFieldError</code> for the error message.
        </p>
      </DocsSection>

      <DocsSection title="Button">
        <Preview>
          <DimeField>
            <DimeFieldLabel htmlFor="ta-button">Message</DimeFieldLabel>
            <DimeTextarea id="ta-button" placeholder="Type your message here." />
            <DimeButton className="w-full">Send message</DimeButton>
          </DimeField>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pair with <code className="font-mono">DimeButton</code> inside a{' '}
          <code className="font-mono">DimeField</code> to create a textarea with a submit button.
        </p>
      </DocsSection>
    </div>
  );
}
